import clsx from "clsx";

import checkCircleIcon from "@pages/onboarding/assets/check-circle-icon.svg";
import grayFailedIcon from "@pages/onboarding/assets/verification-failed-gray-circle.svg";

import PlatformIcon from "./platforms/components/platform-icon";
import { type Platform } from "./platforms/constants";
import { usePlatformsMetadata } from "./platforms/metadata";
import { type PlatformState } from "./platforms/platform-context";

const STATUS_STYLE = {
  configured: {
    bgColor: "alien-2",
    imgSrc: checkCircleIcon,
  },
  notConfigured: {
    bgColor: "interstellar-3",
    imgSrc: grayFailedIcon,
  },
};

const StatusIcon = ({
  title,
  isConfigured,
}: {
  title: string;
  isConfigured: boolean;
}) => {
  const configuredState = isConfigured ? "configured" : "notConfigured";
  return (
    <div className="h-full pl-4">
      <div
        className={clsx(
          "h-full pl-2 pr-4 rounded-3xl justify-start items-center br-16 gap-2 inline-flex",
          "bg-" + STATUS_STYLE[configuredState].bgColor,
        )}
      >
        <div className="w-6 h-6 relative">
          <img
            className="mr-2"
            src={STATUS_STYLE[configuredState].imgSrc}
            alt="status icon"
          />
        </div>
        <div className="flex-col justify-center items-start inline-flex">
          <span className="h-5 text-sm font-semibold leading-tight uppercase">
            {title}
          </span>
          <div
            className={clsx(
              "h-4 text-xs font-semibold uppercase leading-tight",
              {
                "text-gray+1": configuredState === "notConfigured",
              },
            )}
          >
            {configuredState === "configured" ? "Connected" : "Not connected"}
          </div>
        </div>
      </div>
    </div>
  );
};

const GenericStatusIcons = ({
  platform,
  state,
}: {
  platform: Platform;
  state: PlatformState;
}) => {
  const { isConnectionSupported, isUTMSupported } = usePlatformsMetadata();
  return (
    <div className="items-center inline-flex">
      {isConnectionSupported(platform) && (
        <StatusIcon
          title="INTEGRATION"
          isConfigured={state.isConnectionConfigured}
        />
      )}
      {isConnectionSupported(platform) && isUTMSupported(platform) && (
        <div className="ml-4 w-px h-6 bg-gray-0" />
      )}
      {isUTMSupported(platform) && (
        <StatusIcon title="UTMS" isConfigured={state.isUTMsConfigured} />
      )}
    </div>
  );
};

export const PlatformSectionHeader = ({
  platform,
  state,
}: {
  platform: Platform;
  state: PlatformState;
  relatedStates?: PlatformState[];
}) => {
  const { getTitleForPlatform } = usePlatformsMetadata();
  return (
    <div className="self-stretch justify-between items-center inline-flex h-11">
      <div className="flex">
        <div className="justify-start items-center gap-2 flex">
          <PlatformIcon platform={platform as Platform} />
          <div className="text-2xl font-semibold leading-loose">
            {getTitleForPlatform(platform)}
          </div>
        </div>
        <GenericStatusIcons platform={platform} state={state} />
      </div>
    </div>
  );
};
